import React, { useState, useEffect } from 'react';
import {
  Box, Heading, Text, Button, VStack, HStack, SimpleGrid,
  Table, Thead, Tbody, Tr, Th, Td, Badge, IconButton,
  useToast, useDisclosure, Icon, Input, InputGroup, InputLeftElement,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, ModalFooter,
  FormControl, FormLabel, Textarea, Select, Spinner, Flex, Tooltip,
} from '@chakra-ui/react';
import { FaPlus, FaSearch, FaCopy, FaLink, FaHospital, FaEdit, FaTrash, FaCheck, FaTimes, FaKey, FaExternalLinkAlt } from 'react-icons/fa';
import { supabase } from '../../supabaseClient';

export default function FacilityManagement() {
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editFacility, setEditFacility] = useState(null);
  const [passwordFacility, setPasswordFacility] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isPwOpen, onOpen: onPwOpen, onClose: onPwClose } = useDisclosure();
  const toast = useToast();

  const [form, setForm] = useState({
    name: '', address: '', city: '', state: '', zip: '',
    contact_name: '', contact_email: '', contact_phone: '', contact_title: '',
    facility_type: 'nursing_center', notes: '',
  });

  const APP_URL = window.location.hostname === 'localhost'
    ? 'http://localhost:3000'
    : 'https://athomeblooddraw.com';

  useEffect(() => { fetchFacilities(); }, []);

  const fetchFacilities = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('facilities')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setFacilities(data || []);
    } catch (err) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({ name: '', address: '', city: '', state: '', zip: '', contact_name: '', contact_email: '', contact_phone: '', contact_title: '', facility_type: 'nursing_center', notes: '' });
    setEditFacility(null);
  };

  const handleOpen = (facility = null) => {
    if (facility) {
      setEditFacility(facility);
      setForm({
        name: facility.name || '',
        address: facility.address || '',
        city: facility.city || '',
        state: facility.state || '',
        zip: facility.zip || '',
        contact_name: facility.contact_name || '',
        contact_email: facility.contact_email || '',
        contact_phone: facility.contact_phone || '',
        contact_title: facility.contact_title || '',
        facility_type: facility.facility_type || 'nursing_center',
        notes: facility.notes || '',
      });
    } else {
      resetForm();
    }
    onOpen();
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Facility name is required', status: 'warning', duration: 3000 });
      return;
    }

    try {
      if (editFacility) {
        const { error } = await supabase
          .from('facilities')
          .update({ ...form, updated_at: new Date().toISOString() })
          .eq('id', editFacility.id);
        if (error) throw error;
        toast({ title: 'Facility updated', status: 'success', duration: 2000 });
      } else {
        const { error } = await supabase
          .from('facilities')
          .insert(form);
        if (error) throw error;
        toast({ title: 'Facility created', status: 'success', duration: 2000 });
        try {
          await supabase.from('admin_activity_log').insert({
            action: 'Facility created',
            username: JSON.parse(localStorage.getItem('adminUser') || '{}')?.name || 'Admin',
            details: `Created facility: ${form.name}`,
            created_at: new Date().toISOString(),
          });
        } catch (_) { /* ignore log errors */ }
      }
      onClose();
      resetForm();
      fetchFacilities();
    } catch (err) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 3000 });
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      const { error } = await supabase.from('facilities').delete().eq('id', id);
      if (error) throw error;
      toast({ title: 'Facility deleted', status: 'info', duration: 2000 });
      fetchFacilities();
    } catch (err) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 3000 });
    }
  };

  const copyLink = (id) => {
    const link = `${APP_URL}/agreement/facility/${id}`;
    navigator.clipboard.writeText(link);
    toast({ title: 'Agreement link copied!', description: link, status: 'success', duration: 3000 });
  };

  const copyPortalLink = (id) => {
    const link = `${APP_URL}/facility/${id}`;
    navigator.clipboard.writeText(link);
    toast({ title: 'Portal link copied!', description: link, status: 'success', duration: 3000 });
  };

  const openSetPassword = (facility) => {
    setPasswordFacility(facility);
    setNewPassword('');
    setConfirmPassword('');
    onPwOpen();
  };

  const handleSavePassword = async () => {
    if (!newPassword || newPassword.length < 6) { toast({ title: 'Password must be at least 6 characters', status: 'warning', duration: 3000 }); return; }
    if (newPassword !== confirmPassword) { toast({ title: 'Passwords do not match', status: 'warning', duration: 3000 }); return; }
    setSavingPassword(true);
    try {
      const { error } = await supabase.from('facilities').update({ password: newPassword }).eq('id', passwordFacility.id);
      if (error) throw error;
      toast({ title: 'Password set!', description: 'Facility can now log in to their portal.', status: 'success', duration: 3000 });
      onPwClose();
      fetchFacilities();
    } catch (err) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 3000 });
    } finally {
      setSavingPassword(false);
    }
  };

  const filtered = facilities.filter(f =>
    (f.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (f.contact_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (f.city || '').toLowerCase().includes(search.toLowerCase())
  );

  const facilityTypeLabel = {
    nursing_center: 'Nursing Center',
    assisted_living: 'Assisted Living',
    senior_living: 'Senior Living',
    rehabilitation: 'Rehabilitation',
    hospice: 'Hospice',
    clinic: 'Clinic',
    other: 'Other',
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" fontWeight="700">Facilities</Heading>
          <Text color="gray.500" fontSize="sm">Manage nursing centers and facilities</Text>
        </Box>
        <Button colorScheme="teal" leftIcon={<FaPlus />} onClick={() => handleOpen()}>
          Add Facility
        </Button>
      </Flex>

      {/* Stats */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
        <Box bg="white" p={4} borderRadius="xl" boxShadow="sm" border="1px" borderColor="gray.100">
          <Text fontSize="2xl" fontWeight="700" color="teal.600">{facilities.length}</Text>
          <Text fontSize="sm" color="gray.500">Total Facilities</Text>
        </Box>
        <Box bg="white" p={4} borderRadius="xl" boxShadow="sm" border="1px" borderColor="gray.100">
          <Text fontSize="2xl" fontWeight="700" color="green.600">{facilities.filter(f => f.agreement_signed).length}</Text>
          <Text fontSize="sm" color="gray.500">Agreements Signed</Text>
        </Box>
        <Box bg="white" p={4} borderRadius="xl" boxShadow="sm" border="1px" borderColor="gray.100">
          <Text fontSize="2xl" fontWeight="700" color="orange.600">{facilities.filter(f => !f.agreement_signed).length}</Text>
          <Text fontSize="sm" color="gray.500">Pending Signatures</Text>
        </Box>
        <Box bg="white" p={4} borderRadius="xl" boxShadow="sm" border="1px" borderColor="gray.100">
          <Text fontSize="2xl" fontWeight="700" color="blue.600">{facilities.filter(f => f.is_active).length}</Text>
          <Text fontSize="sm" color="gray.500">Active</Text>
        </Box>
      </SimpleGrid>

      {/* Search */}
      <InputGroup mb={4} maxW="400px">
        <InputLeftElement><Icon as={FaSearch} color="gray.400" /></InputLeftElement>
        <Input placeholder="Search by name, contact, or city..." value={search} onChange={(e) => setSearch(e.target.value)} bg="white" />
      </InputGroup>

      {/* Table */}
      {loading ? (
        <Flex py={10} justify="center"><Spinner size="lg" /></Flex>
      ) : filtered.length === 0 ? (
        <Box py={10} textAlign="center">
          <Icon as={FaHospital} boxSize={12} color="gray.300" mb={3} />
          <Text color="gray.500">{search ? 'No facilities match your search' : 'No facilities yet. Click "Add Facility" to create one.'}</Text>
        </Box>
      ) : (
        <Box overflowX="auto" bg="white" borderRadius="xl" boxShadow="sm">
          <Table size="sm">
            <Thead>
              <Tr bg="gray.50">
                <Th py={3}>Facility</Th>
                <Th py={3}>Type</Th>
                <Th py={3}>Contact</Th>
                <Th py={3}>Location</Th>
                <Th py={3}>Secret Key</Th>
                <Th py={3}>Last Accessed</Th>
                <Th py={3}>Agreement</Th>
                <Th py={3}>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filtered.map(f => (
                <Tr key={f.id} _hover={{ bg: 'gray.50' }}>
                  <Td py={3}>
                    <Text fontWeight="600" fontSize="sm">{f.name}</Text>
                    {f.notes && <Text fontSize="xs" color="gray.400" noOfLines={1}>{f.notes}</Text>}
                  </Td>
                  <Td py={3}>
                    <Badge colorScheme="purple" fontSize="xs">{facilityTypeLabel[f.facility_type] || f.facility_type}</Badge>
                  </Td>
                  <Td py={3}>
                    <Text fontSize="sm">{f.contact_name || '—'}</Text>
                    <Text fontSize="xs" color="gray.500">{f.contact_email || ''}</Text>
                    <Text fontSize="xs" color="gray.500">{f.contact_phone || ''}</Text>
                  </Td>
                  <Td py={3}>
                    <Text fontSize="sm">{f.city ? `${f.city}, ${f.state || ''}` : '—'}</Text>
                  </Td>
                  <Td py={3}>
                    {f.password ? (
                      <HStack spacing={1}>
                        <Text fontSize="sm" fontFamily="mono" color="gray.700">{'•'.repeat(f.password.length)}</Text>
                        <Tooltip label="Copy password">
                          <IconButton icon={<FaCopy />} size="xs" variant="ghost" onClick={() => { navigator.clipboard.writeText(f.password); toast({ title: 'Password copied', status: 'success', duration: 2000 }); }} />
                        </Tooltip>
                      </HStack>
                    ) : (
                      <Text fontSize="xs" color="gray.400">No key set</Text>
                    )}
                  </Td>
                  <Td py={3}>
                    <Text fontSize="xs" color="gray.500">{f.updated_at && f.password ? new Date(f.updated_at).toLocaleDateString() : 'Never'}</Text>
                  </Td>
                  <Td py={3}>
                    {f.agreement_signed ? (
                      <VStack spacing={0} align="start">
                        <Badge colorScheme="green" fontSize="xs"><Icon as={FaCheck} mr={1} />Signed</Badge>
                        {f.agreement_signed_at && (
                          <Text fontSize="xs" color="gray.400">{new Date(f.agreement_signed_at).toLocaleDateString()}</Text>
                        )}
                        <Button size="xs" variant="ghost" colorScheme="teal" leftIcon={<FaCopy />} onClick={() => copyLink(f.id)} mt={1}>
                          Copy Link
                        </Button>
                      </VStack>
                    ) : (
                      <VStack spacing={1} align="start">
                        <Badge colorScheme="red" fontSize="xs">NOT SIGNED</Badge>
                        <Button size="xs" variant="ghost" colorScheme="teal" leftIcon={<FaCopy />} onClick={() => copyLink(f.id)}>
                          Copy Link
                        </Button>
                      </VStack>
                    )}
                  </Td>
                  <Td py={3}>
                    <VStack spacing={1} align="start">
                      <HStack spacing={1}>
                        <Tooltip label="Edit">
                          <IconButton icon={<FaEdit />} size="xs" variant="ghost" colorScheme="blue" onClick={() => handleOpen(f)} />
                        </Tooltip>
                        <Tooltip label="Delete">
                          <IconButton icon={<FaTrash />} size="xs" variant="ghost" colorScheme="red" onClick={() => handleDelete(f.id, f.name)} />
                        </Tooltip>
                        {f.password ? (
                          <Button size="xs" colorScheme="orange" leftIcon={<FaKey />} onClick={() => openSetPassword(f)}>
                            Update Key
                          </Button>
                        ) : (
                          <Button size="xs" colorScheme="purple" leftIcon={<FaKey />} onClick={() => openSetPassword(f)}>
                            Set Key
                          </Button>
                        )}
                      </HStack>
                      <Button size="xs" variant="outline" colorScheme="purple" leftIcon={<FaExternalLinkAlt />}
                        onClick={() => window.open(`${APP_URL}/facility/${f.id}`, '_blank')}>
                        View Portal
                      </Button>
                    </VStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Add/Edit Modal */}
      <Modal isOpen={isOpen} onClose={() => { onClose(); resetForm(); }} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editFacility ? 'Edit Facility' : 'Add New Facility'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Facility Name</FormLabel>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Sunrise Senior Living" />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Facility Type</FormLabel>
                <Select value={form.facility_type} onChange={(e) => setForm({ ...form, facility_type: e.target.value })}>
                  <option value="nursing_center">Nursing Center</option>
                  <option value="assisted_living">Assisted Living</option>
                  <option value="senior_living">Senior Living</option>
                  <option value="rehabilitation">Rehabilitation</option>
                  <option value="hospice">Hospice</option>
                  <option value="clinic">Clinic</option>
                  <option value="other">Other</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Address</FormLabel>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="123 Main St" />
              </FormControl>
              <HStack spacing={4} w="full">
                <FormControl>
                  <FormLabel fontSize="sm">City</FormLabel>
                  <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Brooklyn" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">State</FormLabel>
                  <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="NY" maxW="100px" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">ZIP</FormLabel>
                  <Input value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} placeholder="11224" maxW="120px" />
                </FormControl>
              </HStack>
              <SimpleGrid columns={2} spacing={4} w="full">
                <FormControl>
                  <FormLabel fontSize="sm">Contact Name</FormLabel>
                  <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="Diane Smith" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Contact Title</FormLabel>
                  <Input value={form.contact_title} onChange={(e) => setForm({ ...form, contact_title: e.target.value })} placeholder="Administrator" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Contact Email</FormLabel>
                  <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="diane@facility.com" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Contact Phone</FormLabel>
                  <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="(555) 123-4567" />
                </FormControl>
              </SimpleGrid>
              <FormControl>
                <FormLabel fontSize="sm">Notes</FormLabel>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any additional notes..." rows={2} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => { onClose(); resetForm(); }}>Cancel</Button>
            <Button colorScheme="teal" onClick={handleSave}>{editFacility ? 'Save Changes' : 'Create Facility'}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Set Password Modal */}
      <Modal isOpen={isPwOpen} onClose={onPwClose} isCentered size="md" closeOnOverlayClick={!savingPassword}>
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="2xl" overflow="hidden">
          <Box bgGradient="linear(to-r, purple.600, purple.800)" px={6} py={4}>
            <HStack spacing={3}>
              <Icon as={FaKey} color="white" boxSize={5} />
              <Box>
                <Text color="white" fontSize="lg" fontWeight="700">
                  {passwordFacility?.password ? 'Update Portal Key' : 'Set Portal Key'}
                </Text>
                <Text color="whiteAlpha.800" fontSize="sm">{passwordFacility?.name}</Text>
              </Box>
            </HStack>
          </Box>
          <ModalCloseButton color="white" />
          <ModalBody py={5}>
            <VStack spacing={4}>
              {passwordFacility?.password && (
                <Box w="full" bg="gray.50" p={3} borderRadius="md">
                  <Text fontSize="xs" color="gray.500" mb={1}>Current Key</Text>
                  <HStack>
                    <Text fontFamily="mono" fontWeight="600">{'•'.repeat(passwordFacility.password.length)}</Text>
                    <IconButton icon={<FaCopy />} size="xs" variant="ghost" onClick={() => { navigator.clipboard.writeText(passwordFacility.password); toast({ title: 'Copied', status: 'success', duration: 1500 }); }} />
                  </HStack>
                </Box>
              )}
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="600">New Password</FormLabel>
                <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 6 characters" />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="600">Confirm Password</FormLabel>
                <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm password" />
              </FormControl>
              {passwordFacility?.contact_email && (
                <Box w="full" bg="blue.50" p={3} borderRadius="md">
                  <Text fontSize="xs" color="blue.600" fontWeight="600">Login Credentials</Text>
                  <Text fontSize="sm" color="blue.700">Email: {passwordFacility.contact_email}</Text>
                  <Text fontSize="sm" color="blue.700">Password: {newPassword ? '•'.repeat(newPassword.length) + ' (set)' : '(set above)'}</Text>
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onPwClose}>Cancel</Button>
            <Button colorScheme="purple" leftIcon={<Icon as={FaKey} />} onClick={handleSavePassword} isLoading={savingPassword}>
              {passwordFacility?.password ? 'Update Key' : 'Set Key'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
